import {
  ScheduleData,
  DaySchedule,
  WeeklySchedule,
  TimeSlot,
  ScheduleType,
} from './types'
import { DAYS_OF_WEEK, DEFAULT_TIME_SLOT } from './constants'
import {
  convertTo24Hour,
  convertTo12Hour,
  isValidTimeFormat,
  timeToMinutes,
  minutesTo12Hour,
} from './timeUtils'

/**
 * Generates a unique ID for a time slot
 * @returns Unique string ID
 */
export function generateTimeSlotId(): string {
  return `slot-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Safely converts 12-hour time to 24-hour format with error handling
 * @param time - Time in 12-hour format (e.g., "9:00")
 * @param period - AM or PM
 * @returns Object with success status and either converted time or error message
 */
export function safeConvertTo24Hour(
  time: string,
  period: 'AM' | 'PM',
): { success: true; value: string } | { success: false; error: string } {
  try {
    if (!isValidTimeFormat(time)) {
      return { success: false, error: `Invalid time format: ${time}` }
    }
    const value = convertTo24Hour(time, period)
    return { success: true, value }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Time conversion failed',
    }
  }
}

/**
 * Transforms UI schedule format to backend format.
 * Always returns arrays of TimeRange for each day to maintain consistency.
 *
 * @param schedule - UI schedule data
 * @returns Backend weekly schedule format with arrays of TimeRange
 */
export function transformScheduleForBackend(
  schedule: ScheduleData,
): WeeklySchedule {
  const backendSchedule: WeeklySchedule = {}

  schedule.days.forEach(day => {
    const dayKey = day.id.toLowerCase() as keyof WeeklySchedule
    if (day.enabled && day.timeSlots.length > 0) {
      try {
        backendSchedule[dayKey] = day.timeSlots.map(slot => {
          if (slot.allDay) {
            return { allDay: true }
          }

          const startTimeResult = safeConvertTo24Hour(
            slot.startTime,
            slot.startPeriod,
          )
          const endTimeResult = safeConvertTo24Hour(
            slot.endTime,
            slot.endPeriod,
          )

          if (!startTimeResult.success || !endTimeResult.success) {
            throw new Error(
              `Invalid time format for ${day.label}: ${
                startTimeResult.success ? '' : startTimeResult.error
              } ${endTimeResult.success ? '' : endTimeResult.error}`.trim(),
            )
          }

          return {
            allDay: false,
            startTime: startTimeResult.value,
            endTime: endTimeResult.value,
          }
        })
      } catch (error) {
        // console.error(`Error transforming schedule for ${day.label}:`, error)
        // Fallback to empty array for this day
        backendSchedule[dayKey] = []
      }
    } else {
      backendSchedule[dayKey] = []
    }
  })

  return backendSchedule
}

/**
 * Normalizes backend schedule data to ensure consistent array format.
 * The backend may send either single TimeRange objects or arrays of TimeRange.
 * This function ensures we always work with arrays internally.
 *
 * Example inputs and outputs:
 * Input:  { monday: { startTime: "09:00", endTime: "17:00" } }
 * Output: { monday: [{ startTime: "09:00", endTime: "17:00" }] }
 *
 * Input:  { monday: [{ startTime: "09:00", endTime: "17:00" }] }
 * Output: { monday: [{ startTime: "09:00", endTime: "17:00" }] }
 *
 * @param backendSchedule - Backend weekly schedule that may contain single objects or arrays
 * @returns Normalized backend schedule with arrays of TimeRange
 */
export function normalizeBackendSchedule(
  backendSchedule: WeeklySchedule,
): WeeklySchedule {
  const normalized: WeeklySchedule = {}

  Object.entries(backendSchedule).forEach(([day, timeRanges]) => {
    // If timeRanges is an object (not array), wrap it in an array
    normalized[day as keyof WeeklySchedule] = Array.isArray(timeRanges)
      ? timeRanges
      : [timeRanges]
  })

  return normalized
}

/**
 * Transforms backend schedule format to UI format.
 * First normalizes the backend data to ensure arrays, then converts to UI format.
 *
 * Data flow:
 * 1. Backend data (single object or array) → normalizeBackendSchedule → arrays
 * 2. Arrays → convert times to 12-hour format → UI format
 *
 * @param backendSchedule - Backend weekly schedule
 * @returns UI schedule data
 */
export function transformBackendScheduleToUI(
  backendSchedule: WeeklySchedule,
): ScheduleData {
  // First normalize the backend data to ensure arrays
  const normalizedBackendSchedule = normalizeBackendSchedule(backendSchedule)

  const days = DAYS_OF_WEEK.map(dayTemplate => {
    const dayKey = dayTemplate.id.toLowerCase() as keyof WeeklySchedule
    const backendDay = normalizedBackendSchedule[dayKey]

    if (!backendDay || (Array.isArray(backendDay) && backendDay.length === 0)) {
      // If no schedule for this day, it should be disabled
      return {
        id: dayTemplate.id,
        label: dayTemplate.label,
        enabled: false,
        timeSlots: [], // Empty array for disabled days
      }
    }

    // At this point, backendDay is guaranteed to be an array
    const timeRanges = Array.isArray(backendDay) ? backendDay : [backendDay]

    return {
      id: dayTemplate.id,
      label: dayTemplate.label,
      enabled: true,
      timeSlots: timeRanges.map((tr, slotIndex) => ({
        id: generateTimeSlotId(),
        allDay: tr.allDay,
        startTime: tr.allDay ? '' : convertTo12Hour(tr.startTime!).time,
        startPeriod: tr.allDay
          ? ('AM' as const)
          : convertTo12Hour(tr.startTime!).period,
        endTime: tr.allDay ? '' : convertTo12Hour(tr.endTime!).time,
        endPeriod: tr.allDay
          ? ('PM' as const)
          : convertTo12Hour(tr.endTime!).period,
      })),
    }
  })
  return { days }
}

/**
 * Gets default schedule with Mon-Fri 9-5
 * @returns Default schedule data
 */
export function getDefaultSchedule(): ScheduleData {
  return {
    days: DAYS_OF_WEEK.map((day, index) => ({
      id: day.id,
      label: day.label,
      enabled: index < 5, // Mon-Fri enabled
      timeSlots:
        index < 5
          ? [
              {
                id: generateTimeSlotId(),
                ...DEFAULT_TIME_SLOT,
              },
            ]
          : [], // Empty array for disabled days
    })),
  }
}

/**
 * Gets an empty schedule with all days disabled
 * @returns Empty schedule data (all days disabled)
 */
export function getEmptySchedule(): ScheduleData {
  return {
    days: DAYS_OF_WEEK.map(day => ({
      id: day.id,
      label: day.label,
      enabled: false, // All days disabled
      timeSlots: [], // Empty array for all days
    })),
  }
}

/**
 * Generates default days with Mon-Fri enabled
 * @returns Array of day schedules
 */
export function generateDefaultDays(): DaySchedule[] {
  return DAYS_OF_WEEK.map((day, index) => ({
    id: day.id,
    label: day.label,
    enabled: index < 5, // Mon-Fri enabled
    timeSlots:
      index < 5
        ? [
            {
              id: generateTimeSlotId(),
              ...DEFAULT_TIME_SLOT,
            },
          ]
        : [], // Empty array for disabled days
  }))
}

/**
 * Checks if two time slots overlap
 */
function doTimeSlotsOverlap(slot1: TimeSlot, slot2: TimeSlot): boolean {
  if (slot1.allDay || slot2.allDay) return true

  const start1 = timeToMinutes(slot1.startTime, slot1.startPeriod)
  const end1 = timeToMinutes(slot1.endTime, slot1.endPeriod, true)
  const start2 = timeToMinutes(slot2.startTime, slot2.startPeriod)
  const end2 = timeToMinutes(slot2.endTime, slot2.endPeriod, true)

  return start1 < end2 && start2 < end1
}

/**
 * Validates a schedule for correctness
 * @param schedule - Schedule to validate
 * @returns true if schedule is valid, false otherwise
 */
export function validateSchedule(schedule: ScheduleData): boolean {
  return schedule.days.every(day => {
    if (!day.enabled) return true

    // Check for overlapping time slots
    const slots = day.timeSlots.filter(slot => !slot.allDay)
    for (let i = 0; i < slots.length; i += 1) {
      for (let j = i + 1; j < slots.length; j += 1) {
        if (doTimeSlotsOverlap(slots[i], slots[j])) {
          return false
        }
      }
    }
    return true
  })
}

/**
 * Inverts a business hours schedule to produce an after-hours schedule.
 * For each day, generates all time slots not covered by business hours.
 * If a day is all-day (24h), the after-hours is empty (disabled).
 */
export function invertSchedule(schedule: ScheduleData): ScheduleData {
  function getAfterHoursSlots(
    daySlots: DaySchedule['timeSlots'],
    dayIndex: number,
  ): DaySchedule['timeSlots'] {
    // If no business hours, after hours is all day
    if (!daySlots || daySlots.length === 0) {
      return [
        {
          id: generateTimeSlotId(),
          allDay: true,
          startTime: '',
          startPeriod: 'AM',
          endTime: '',
          endPeriod: 'PM',
        } as TimeSlot,
      ]
    }
    // Build sorted list of business hour ranges
    const ranges = daySlots
      .filter(slot => !slot.allDay)
      .map(slot => ({
        start: timeToMinutes(slot.startTime, slot.startPeriod),
        end: timeToMinutes(slot.endTime, slot.endPeriod, true),
      }))
      .sort((a, b) => a.start - b.start)
    // If any allDay slot, after hours is empty
    if (daySlots.some(slot => slot.allDay)) return []
    // Find gaps between business hour ranges
    const afterHours: DaySchedule['timeSlots'] = []
    let prevEnd = 0
    ranges.forEach(range => {
      if (range.start > prevEnd) {
        afterHours.push({
          id: generateTimeSlotId(),
          allDay: false,
          startTime: minutesTo12Hour(prevEnd).time,
          startPeriod: minutesTo12Hour(prevEnd).period,
          endTime: minutesTo12Hour(range.start).time,
          endPeriod: minutesTo12Hour(range.start).period,
        })
      }
      prevEnd = range.end
    })
    // Add after-hours slot from last business hour end to midnight
    if (prevEnd < 24 * 60) {
      afterHours.push({
        id: generateTimeSlotId(),
        allDay: false,
        startTime: minutesTo12Hour(prevEnd).time,
        startPeriod: minutesTo12Hour(prevEnd).period,
        endTime: minutesTo12Hour(0).time,
        endPeriod: minutesTo12Hour(0).period,
      })
    }
    // Remove zero-length slots
    return afterHours.filter(
      slot =>
        slot.startTime !== slot.endTime || slot.startPeriod !== slot.endPeriod,
    )
  }
  return {
    days: schedule.days.map((day: DaySchedule, dayIndex: number) => {
      // If the day is disabled in business hours, after-hours should be all day
      const afterHoursSlots = day.enabled
        ? getAfterHoursSlots(day.timeSlots, dayIndex)
        : [
            {
              id: generateTimeSlotId(),
              allDay: true,
              startTime: '',
              startPeriod: 'AM',
              endTime: '',
              endPeriod: 'PM',
            } as TimeSlot,
          ]
      return {
        ...day,
        enabled: afterHoursSlots.length > 0,
        timeSlots: afterHoursSlots.length > 0 ? afterHoursSlots : [],
      }
    }),
  }
}

/**
 * Returns a schedule where all days are enabled and all have a single all-day slot.
 */
export function getAlwaysSchedule(): ScheduleData {
  return {
    days: DAYS_OF_WEEK.map((day, index) => ({
      id: day.id,
      label: day.label,
      enabled: true,
      timeSlots: [
        {
          id: generateTimeSlotId(),
          allDay: true,
          startTime: '',
          startPeriod: 'AM',
          endTime: '',
          endPeriod: 'PM',
        },
      ],
    })),
  }
}

/**
 * Creates a "clean" version of a schedule suitable for comparison by setting random IDs to a fixed value
 * and normalizing the structure to ensure consistent comparison.
 */
function normalizeScheduleForComparison(schedule: ScheduleData): ScheduleData {
  return {
    days: schedule.days
      .sort((a, b) => a.id.localeCompare(b.id)) // Sort days for consistent order
      .map(day => ({
        id: day.id,
        label: day.label,
        enabled: day.enabled,
        timeSlots: day.timeSlots
          .sort((a, b) => {
            // Sort time slots for consistent comparison
            if (a.allDay !== b.allDay) return a.allDay ? -1 : 1
            if (a.startTime !== b.startTime)
              return a.startTime.localeCompare(b.startTime)
            return a.startPeriod.localeCompare(b.startPeriod)
          })
          .map(slot => ({
            id: 'normalized-id-for-comparison',
            allDay: slot.allDay,
            startTime: slot.startTime,
            startPeriod: slot.startPeriod,
            endTime: slot.endTime,
            endPeriod: slot.endPeriod,
          })),
      })),
  }
}

/**
 * Determines the schedule type from a loaded schedule and company business hours.
 */
export function getScheduleTypeFromSchedule(
  schedule: ScheduleData,
  companyBusinessHours: ScheduleData,
): ScheduleType {
  // Always: all days enabled, all have a single allDay slot
  const allDaysAlways = schedule.days.every(
    day => day.enabled && day.timeSlots.length === 1 && day.timeSlots[0].allDay,
  )
  if (allDaysAlways) return 'always'

  const normalizedSchedule = normalizeScheduleForComparison(schedule)
  const normalizedBusinessHours =
    normalizeScheduleForComparison(companyBusinessHours)

  // Business hours: matches company business hours
  const isBusinessHours =
    JSON.stringify(normalizedSchedule) ===
    JSON.stringify(normalizedBusinessHours)
  if (isBusinessHours) return 'business-hours'

  // After hours: matches invertSchedule(company business hours)
  const isAfterHours =
    JSON.stringify(normalizedSchedule) ===
    JSON.stringify(
      normalizeScheduleForComparison(invertSchedule(companyBusinessHours)),
    )
  if (isAfterHours) return 'after-hours'

  // Custom: anything else
  return 'custom'
}

/**
 * Infers scheduleType from backend rule and company business hours.
 * Priority: 1. Use schedule when present, 2. Else use applyDuring flag
 */
export function getScheduleTypeFromBackendRule(
  rule: any, // APIHandoffRule
  companyBusinessHours: ScheduleData,
): ScheduleType {
  // Priority 1: Use schedule when present
  if (rule.timeRestrictions?.schedule) {
    const schedule = transformBackendScheduleToUI(
      rule.timeRestrictions.schedule,
    )

    // Check if it's an "always" schedule (all days enabled, all have single allDay slot)
    if (
      schedule.days.every(
        day =>
          day.enabled && day.timeSlots.length === 1 && day.timeSlots[0].allDay,
      )
    ) {
      return 'always'
    }

    // For business-hours and after-hours comparison, we need to normalize both schedules
    // because the IDs will be different after transformation
    const normalizedSchedule = normalizeScheduleForComparison(schedule)
    const normalizedBusinessHours =
      normalizeScheduleForComparison(companyBusinessHours)

    // Check if it matches business hours
    if (
      JSON.stringify(normalizedSchedule) ===
      JSON.stringify(normalizedBusinessHours)
    ) {
      return 'business-hours'
    }

    // Check if it matches after-hours (inverse of business hours)
    const normalizedAfterHours = normalizeScheduleForComparison(
      invertSchedule(companyBusinessHours),
    )
    if (
      JSON.stringify(normalizedSchedule) ===
      JSON.stringify(normalizedAfterHours)
    ) {
      return 'after-hours'
    }

    // Must be custom
    return 'custom'
  }

  // Priority 2: Use applyDuring flag when present
  if (rule.timeRestrictions?.applyDuring) {
    switch (rule.timeRestrictions.applyDuring) {
      case 'business-hours':
        return 'business-hours'
      case 'after-hours':
        return 'after-hours'
      case 'always':
        return 'always'
      case 'custom-time-restrictions':
        return 'custom'
      default:
        return 'business-hours' // fallback
    }
  }

  // Legacy fallback: assume business-hours if no schedule and no applyDuring
  return 'business-hours'
}

/**
 * Gets the schedule for display based on type.
 *
 * For 'business-hours' and 'after-hours', this uses fresh business hours when provided,
 * ensuring these rule types always stay in sync with company settings.
 */
export function getScheduleForDisplay(
  scheduleType: ScheduleType,
  companyBusinessHours: ScheduleData,
  savedSchedule: ScheduleData,
  freshBusinessHours?: ScheduleData, // Optional fresh data from backend
): ScheduleData {
  const businessHours = freshBusinessHours || companyBusinessHours

  switch (scheduleType) {
    case 'business-hours':
      return businessHours
    case 'after-hours':
      return invertSchedule(businessHours)
    case 'always':
      return getAlwaysSchedule()
    case 'custom':
    default:
      return savedSchedule
  }
}

/**
 * Formats a schedule for human-readable display.
 */
export function scheduleSummary(schedule: ScheduleData): string {
  return schedule.days
    .filter(day => day.enabled)
    .map(day => {
      if (day.timeSlots.length === 1 && day.timeSlots[0].allDay) {
        return `${day.label}: all day`
      }
      const slots = day.timeSlots
        .map(slot =>
          slot.allDay
            ? 'all day'
            : `${slot.startTime}${slot.startPeriod.toLowerCase()}-${
                slot.endTime
              }${slot.endPeriod.toLowerCase()}`,
        )
        .join(', ')
      return `${day.label}: ${slots}`
    })
    .join('\n')
}
